import { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { getBlogLeadMagnet, getBlogLeadMagnetDownloadUrl } from '@/lib/blogLeadMagnets';
import {
  buildLeadMagnetEmailJsParams,
  generateLeadId,
  persistLeadAttribution,
  readLeadAttribution,
} from '@/lib/leadAttribution';
import { trackLeadMagnetDownload, trackLeadMagnetEmailError } from '@/lib/analyticsEvents';

type BlogLeadMagnetProps = {
  blogSlug: string;
};

function triggerDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

const BlogLeadMagnet = ({ blogSlug }: BlogLeadMagnetProps) => {
  const { lang } = useLanguage();
  const { toast } = useToast();
  const magnet = getBlogLeadMagnet(blogSlug);
  const locale = lang === 'tr' ? 'tr' : 'en';
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const copy =
    locale === 'tr'
      ? {
          tag: 'Ücretsiz kaynak',
          emailLabel: 'E-posta adresiniz',
          emailPlaceholder: 'ornek@sirket.com',
          cta: 'Checklist’i indir',
          submitting: 'Hazırlanıyor...',
          privacy: 'Spam yok. Kaynağı indirmek için bir kez e-posta istiyoruz.',
          successTitle: 'İndirme başladı',
          successDesc: 'Kaynak cihazınıza indiriliyor. Ekibimiz gerekirse bu e-posta adresinden size ulaşacak.',
          successDescNoNotify: 'Kaynak cihazınıza indiriliyor.',
          invalidEmail: 'Geçerli bir e-posta girin.',
          errorTitle: 'İndirme başarısız',
          errorDesc: 'Lütfen tekrar deneyin veya doğrudan iletişime geçin.',
        }
      : {
          tag: 'Free resource',
          emailLabel: 'Your email',
          emailPlaceholder: 'you@company.com',
          cta: 'Download checklist',
          submitting: 'Preparing...',
          privacy: 'No spam. We ask for email once to unlock the download.',
          successTitle: 'Download started',
          successDesc: 'Your resource is downloading to your device. Our team may follow up at this email.',
          successDescNoNotify: 'Your resource is downloading to your device.',
          invalidEmail: 'Enter a valid email address.',
          errorTitle: 'Download failed',
          errorDesc: 'Please try again or contact us directly.',
        };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      toast({ title: copy.invalidEmail });
      return;
    }

    setIsSubmitting(true);

    const downloadUrl = getBlogLeadMagnetDownloadUrl(magnet, locale);
    try {
      triggerDownload(downloadUrl, magnet.fileName[locale]);
    } catch {
      toast({ title: copy.errorTitle, description: copy.errorDesc, variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }
    trackLeadMagnetDownload(blogSlug, magnet.id, locale);
    setUnlocked(true);

    const priorAttribution = readLeadAttribution();
    persistLeadAttribution({
      ...priorAttribution,
      source: 'blog_lead_magnet',
      blogSlug,
      landingPath:
        priorAttribution?.landingPath ??
        (typeof window !== 'undefined' ? window.location.pathname : undefined),
    });

    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as string | undefined;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined;
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string | undefined;

    let notified = false;
    if (serviceId && templateId && publicKey) {
      try {
        await emailjs.send(
          serviceId,
          templateId,
          buildLeadMagnetEmailJsParams({
            lang: locale,
            email: trimmedEmail,
            blogSlug,
            magnetId: magnet.id,
            resourceTitle: magnet.title[locale],
            downloadUrl,
            leadAttribution: priorAttribution,
            targetEmail: 'contact@figtures.com',
            leadId: generateLeadId(),
          }),
          { publicKey },
        );
        notified = true;
      } catch (error) {
        console.warn('[BlogLeadMagnet] EmailJS notification failed', error);
        trackLeadMagnetEmailError(blogSlug, magnet.id, locale);
      }
    }

    toast({
      title: copy.successTitle,
      description: notified ? copy.successDesc : copy.successDescNoNotify,
    });
    setIsSubmitting(false);
  };

  return (
    <section className="mt-16 max-w-3xl rounded-2xl border border-border bg-gradient-to-br from-secondary/40 to-card/80 p-8 sm:p-10">
      <div className="flex items-start gap-4 mb-6">
        <div className="rounded-xl border border-border/70 bg-background p-3 shrink-0">
          <FileText className="h-6 w-6 text-foreground" aria-hidden />
        </div>
        <div>
          <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
            {copy.tag}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {magnet.title[locale]}
          </h2>
          <p className="font-body text-muted-foreground leading-relaxed">{magnet.description[locale]}</p>
        </div>
      </div>

      <ul className="mb-8 space-y-2 font-body text-sm text-foreground/85">
        {magnet.bullets[locale].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-foreground/60 shrink-0" aria-hidden />
            {item}
          </li>
        ))}
      </ul>

      {unlocked ? (
        <a
          href={getBlogLeadMagnetDownloadUrl(magnet, locale)}
          download={magnet.fileName[locale]}
          className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 font-body text-sm text-foreground hover:bg-secondary/50 transition-colors"
        >
          <Download size={16} />
          {locale === 'tr' ? 'Tekrar indir' : 'Download again'}
        </a>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor={`lead-magnet-email-${blogSlug}`} className="sr-only">
              {copy.emailLabel}
            </label>
            <input
              id={`lead-magnet-email-${blogSlug}`}
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={copy.emailPlaceholder}
              className="w-full rounded-full border border-border bg-background px-5 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-foreground/20"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-8 py-3 font-body text-sm font-medium text-background disabled:opacity-60"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
            {isSubmitting ? copy.submitting : copy.cta}
          </button>
        </form>
      )}

      <p className="mt-4 font-body text-xs text-muted-foreground">{copy.privacy}</p>
    </section>
  );
};

export default BlogLeadMagnet;
